import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * MedicineSearch — search a medicine or an Rx Group and get either
 * a Medicine object OR an array of drugs (from Rx group).
 * onPickMedicine(medicine) -> pass full medicine record
 * onPickGroup(group)       -> pass full group record (with drugs)
 */
export default function MedicineSearch({ onPickMedicine, onPickGroup, mode: initialMode = "all", placeholder = "Enter first 3 characters to search" }) {
  const [q, setQ] = useState("");
  const [mode, setMode] = useState(initialMode); // all | drugs | rx
  const [meds, setMeds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (q.trim().length < 3) { setMeds([]); setGroups([]); return; }
    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const tasks = [];
        if (mode !== "rx") tasks.push(api.get(`/medicines?q=${encodeURIComponent(q)}&limit=20`).then((r) => r.data));
        else tasks.push(Promise.resolve([]));
        if (mode !== "drugs") tasks.push(api.get(`/rx-groups`).then((r) => r.data.filter((g) => (g.name || "").toLowerCase().includes(q.toLowerCase()))));
        else tasks.push(Promise.resolve([]));
        const [m, g] = await Promise.all(tasks);
        if (!cancelled) { setMeds(m); setGroups(g); setOpen(true); }
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [q, mode]);

  const pickMed = (m) => { onPickMedicine?.(m); setQ(""); setOpen(false); };
  const pickGroup = (g) => { onPickGroup?.(g); setQ(""); setOpen(false); };

  return (
    <div ref={boxRef} className="relative" data-testid="medicine-search">
      <div className="flex items-center gap-2 bg-white border border-[#E8E6E1] rounded-xl px-3 py-1.5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim().length >= 3 && setOpen(true)}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none text-sm py-1.5"
          data-testid="medicine-search-input"
        />
        <div className="hidden md:flex items-center gap-1 mr-1">
          {["all", "drugs", "rx"].map((k) => (
            <button
              key={k} type="button" onClick={() => setMode(k)}
              className={`text-[11px] font-semibold tracking-wider uppercase px-2 py-1 rounded-md border ${mode === k ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1] text-[#47554D]"}`}
              data-testid={`medicine-search-mode-${k}`}
            >{k === "rx" ? "Rx Group" : k}</button>
          ))}
        </div>
        {busy ? <Loader2 size={16} className="animate-spin text-[#8A9992]" /> : <Search size={16} className="text-[#8A9992]" />}
      </div>
      {open && (meds.length > 0 || groups.length > 0) && (
        <div className="absolute z-50 mt-1 w-full max-h-96 overflow-y-auto card-soft border border-[#EFECE5] p-2">
          {groups.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] tracking-widest uppercase text-[#8A9992] px-2 py-1">Rx Groups</div>
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => pickGroup(g)}
                  className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F3F1EC] flex items-center justify-between"
                  data-testid={`rx-group-suggest-${g.id}`}
                >
                  <span className="font-semibold text-sm text-[#0A4C36]">{g.name}</span>
                  <span className="text-xs text-[#8A9992]">{(g.drugs || []).length} drugs</span>
                </button>
              ))}
            </div>
          )}
          {meds.length > 0 && (
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#8A9992] px-2 py-1">Medicines</div>
              {meds.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMed(m)}
                  className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F3F1EC]"
                  data-testid={`medicine-suggest-${m.id}`}
                >
                  <div className="font-semibold text-sm text-[#1F2A24]">{m.name}</div>
                  <div className="text-xs text-[#8A9992]">{m.form} · {(m.strengths || []).join(", ")} · {m.category}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {open && q.trim().length >= 3 && !busy && meds.length === 0 && groups.length === 0 && (
        <div className="absolute z-50 mt-1 w-full card-soft border border-[#EFECE5] p-4 text-xs text-[#8A9992]">No matches for “{q}”.</div>
      )}
    </div>
  );
}
